import { gql } from "@apollo/client"
import { Portal, PortalProps } from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { getAdminUrl } from "@pathwright/utils/src/url/admin"
import useHoverCardOrPopover from "@pathwright/ux/src/components/chakra/useHoverCardOrPopover"
import getPWAdminSSOUrl from "@pathwright/auth/src/views/auth/utils/getPWAdminSSOUrl"
import { Fragment } from "react"
import {
  ContextFragment,
  PathItemType,
  ResourceFragment,
  SchoolFragment
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getApolloClient } from "../pathwright/PathwrightClient"
import { PWAdminCmdKMenu } from "./PWAdminCmdK"
import { usePWAdminCmdKContext } from "./PWAdminCmdKContext"
import PWAdminCmdKItems from "./PWAdminCmdKItems"
import "./PWAdminHint.css"
import { PWAdminCmdKOption, PWAdminHintModel } from "./types"
import useIsPWAdminUtilEnabled from "./useIsPWAdminUtilEnabled"
import useSupportInfo from "./useSupportInfo"

// The PWAdminHint will wrap a component providing a popover exposed on hover
// that will provide different hints or information to the user, such as admin
// links to the related model (i.e. if you were to wrap a Path with PWAdminHint,
// you'd see admin links to that Path's model.)

type GetWorkspaceLinkProps = {
  userEmail?: string
  spaceId: number
  componentName: string
  componentProps: Record<string, any>
}

// Get a link to a web dev workspace component with given props.
function getWorkspaceLink({
  userEmail = "",
  spaceId,
  componentName,
  componentProps
}: GetWorkspaceLinkProps) {
  const queryParams = new URLSearchParams()
  queryParams.append("space_id", spaceId.toString())
  queryParams.append("component_name", componentName)
  queryParams.append("component_props", JSON.stringify(componentProps))
  const url = `http://localhost:3000?${queryParams.toString()}`

  return getPWAdminSSOUrl(`admin::${userEmail}`, { url })
}

type PWAdminHintProps = {
  children: React.ReactNode
  model: PWAdminHintModel
  // Whether to wrap the children in a div in order to ensure HoverCard.Trigger works.
  // Note that the children will only ever be wrapped if the hint is enabled, so do not fear
  // any (potential) layout shifts when hovering over the component for non-superusers.
  wrap?: boolean
  // Optionally portal the popover to the end of the DOM.
  portal?: Partial<PortalProps> | boolean
  // Whether to use the children as the trigger for the popover.
  triggerAsChild?: boolean
}

export function getModelInfo(
  model: PWAdminHintModel,
  context: ContextFragment & { school: SchoolFragment } // Ensure school is required/non nullable.
): {
  id: string | number
  type: string
  label: string
  options: PWAdminCmdKOption[]
} {
  const { school, me } = context
  const typename = model.__typename!

  function getUILink(url: string): PWAdminCmdKOption {
    return {
      label: "View in UI",
      icon: "external-link",
      href: url
    }
  }

  function getAdminLink(url: string): PWAdminCmdKOption {
    return {
      label: "View in Admin",
      icon: "external-link",
      href: url,
      target: "_blank"
    }
  }

  switch (model.__typename) {
    case "School":
      return {
        type: "School",
        id: model.id,
        label: model.name,
        options: [
          getAdminLink(
            getAdminUrl(`admin/school/school/${model.id}/`).toString()
          ),
          getUILink(model.url!)
        ]
      }
    case "Resource":
      return {
        type: "Resource",
        id: model.id,
        label: model.name,
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/course/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}library/${model.slug}/`)
        ]
      }
    case "User":
      const options: PWAdminCmdKOption[] = [
        getAdminLink(getAdminUrl(`admin/auth/user/${model.id}/`).toString()),
        getUILink(model.email!)
      ]

      if (model.email) {
        options.push({
          type: "other" as PWAdminCmdKOption["type"],
          label: "Authenticate as User",
          icon: "avatar",
          href: getPWAdminSSOUrl(`admin::${model.email}`)
        })
      }

      return {
        type: "User",
        id: model.id,
        label: model.full_name,
        options
      }
    case "Banner":
      return {
        type: "Banner",
        id: model.id!,
        label: model.heading || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/library/banner/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}manage/library/banner/${model.id!}/`)
        ]
      }
    case "Author":
      return {
        type: "Author",
        id: model.id!,
        label: model.full_name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/courseauthor/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}manage/library/author/${model.id!}/`)
        ]
      }
    case "Category":
      return {
        type: "Category",
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/coursecategory/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}manage/library/category/${model.id!}/`)
        ]
      }
    case "PathItem":
      // The PathItem has no indication as to if it is on a Collectipn path or a regular resource path.
      // So, we need to check the apollo cache for the associated resource type.
      const resourceId = model.context?.resource_id
      const client = getApolloClient()
      const resource = client.cache.readFragment<
        Pick<ResourceFragment, "id" | "resource_type">
      >({
        fragment: gql`
          fragment Resource on Resource {
            id
            resource_type
          }
        `,
        id: `Resource:${resourceId}`
      })
      const isCollectionItem = resource?.resource_type === 30 // 30 is the value for the Collection resource type.

      let uiLink = ""

      if (isCollectionItem) {
        switch (model.type) {
          case PathItemType.Resource:
            uiLink = `${school.url!}library/${model.linked_resource?.id}/${
              model.linked_cohort?.id
            }/path/`
            break
          default:
            uiLink = `${school.url!}library/${model.context?.resource_id}/${
              model.context?.cohort_id
            }/path/`
            break
        }
      } else {
        const itemLabel = model.type === PathItemType.Lesson ? "lesson" : "step"
        switch (model.type) {
          case PathItemType.Divider:
            uiLink = `${school.url!}library/${model.context?.resource_id}/${
              model.context?.cohort_id
            }/path/`
            break
          case PathItemType.Lesson:
          case PathItemType.Step:
            uiLink = `${school.url!}library/${model.context?.resource_id}/${
              model.context?.cohort_id
            }/path/${itemLabel}/${model.source_id!}/`
            break
          case PathItemType.Resource:
            uiLink = `${school.url!}library/${model.linked_resource?.id}/${
              model.linked_cohort?.id
            }/path/`
            break
        }
      }

      return {
        type: `Path Item (${model.type})`,
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/scheduleditem/${model.id}/`).toString()
          ),
          getUILink(uiLink),
          // View all completions in Inbox for a given source item (so across all Cohorts), whether archived or not.
          {
            label: "View all completions in Inbox",
            icon: "envelope",
            to: `/inbox/?source_id=${model.source_id}&filter=all&archived_date_filter={"eq":null},{"op":"or"},{"neq":null}`
          }
        ]
      }
    case "StripeProduct":
      return {
        type: "StripeProduct",
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/payment/stripeproduct/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}subscribe/`),
          {
            label: "View in Stripe Dashboard",
            icon: "external-link",
            href: model.stripe_connected_account_url || ""
          }
        ]
      }
    case "StripePrice":
      return {
        type: "StripePrice",
        id: model.id!,
        label: model.nickname || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/payment/stripeprice/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}subscribe/`),
          {
            label: "View in Stripe Dashboard",
            icon: "external-link",
            href: model.stripe_connected_account_url || ""
          }
        ]
      }
    case "SubscriptionPlan":
      return {
        type: "SchoolSubscriptionPlan",
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(
              `admin/payment/subscriptionplan/${model.id}/`
            ).toString()
          ),
          getUILink(`${school.url!}subscribe/`),
          {
            label: "View in Stripe Dashboard",
            icon: "external-link",
            href: model.stripe_connected_account_url || ""
          }
        ]
      }
    case "Discussion":
      return {
        type: "Discussion",
        id: model.id!,
        label: model.title || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/discussion/discussion/${model.id}/`).toString()
          ),
          getUILink(
            `${school.url!}library/${model.resource_id}/${
              model.cohort_id
            }/community/discussion/${model.id}/`
          ),
          {
            label: "View in Workspace (Dev)",
            icon: "code",
            href: getWorkspaceLink({
              userEmail: me?.email || "",
              spaceId: school.id,
              componentName: "DiscussionPanelWorkspace",
              componentProps: {
                resourceId: model.resource_id,
                cohortId: model.cohort_id,
                discussionId: model.id
              }
            })
          }
        ]
      }
    case "Page":
      return {
        type: "Page",
        id: model.id!,
        label: model.title || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/school/schoolpage/${model.id}/`).toString()
          )
          // Currently no UI link for pages, so just the admin link.
        ]
      }
    default:
      // Struggling to get the exhaustiveGuard working without losing typing of model
      // within the switch cases.
      return {
        id: -1,
        type: "Unknown",
        label: "Unknown",
        options: []
      }
  }
}

function PWAdminHintContent({ model }: { model: PWAdminHintModel }) {
  const pwContext = usePathwrightContext()
  const { setHintsActive } = usePWAdminCmdKContext()
  const data = getModelInfo(model, pwContext)
  const supportInfoOption = useSupportInfo(model)
  const options = [...data.options, supportInfoOption]

  // Disable hints when pressing Escape with hint open.
  useKeyControl("Escape", () => setHintsActive(false))

  return (
    <PWAdminCmdKMenu>
      <PWAdminCmdKItems options={options} />
    </PWAdminCmdKMenu>
  )
}

function PWAdminHint({
  children,
  triggerAsChild,
  model,
  wrap,
  portal
}: PWAdminHintProps) {
  const { hintsActive } = usePWAdminCmdKContext()

  const MaybePortal = portal ? Portal : Fragment
  const portalProps = portal === true ? {} : portal

  const HoverCardOrPopover = useHoverCardOrPopover()
  const triggerProps = triggerAsChild ? { asChild: true } : { p: 0, w: "100%" }

  return useIsPWAdminUtilEnabled() && hintsActive ? (
    <HoverCardOrPopover.Root lazyMount>
      <HoverCardOrPopover.Trigger {...triggerProps}>
        {wrap ? <div>{children}</div> : children}
      </HoverCardOrPopover.Trigger>
      <MaybePortal {...portalProps}>
        <HoverCardOrPopover.Positioner>
          <HoverCardOrPopover.Content
            border="none"
            borderRadius="card"
            // May want to remove boxShadow, but it helps with contrast against
            // dark bg.
            boxShadow="0 0 1px 2px var(--chakra-colors-white-alpha-600)"
            p={0}
            css={{
              "--popover-bg": "black",
              "--hovercard-bg": "black"
            }}
          >
            <HoverCardOrPopover.Arrow
            // border="none"
            // backgroundColor="black"
            // // shadowColor="black"
            // borderColor="black"
            />
            <PWAdminHintContent model={model} />
          </HoverCardOrPopover.Content>
        </HoverCardOrPopover.Positioner>
      </MaybePortal>
    </HoverCardOrPopover.Root>
  ) : (
    children
  )
}

export default PWAdminHint
